import React, { useState } from 'react';
import {
  Box, Typography, IconButton, Button, Chip, Rating,
  SwipeableDrawer, Divider, Menu, MenuItem, ListItemIcon, ListItemText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DirectionsIcon from '@mui/icons-material/Directions';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InstagramIcon from '@mui/icons-material/Instagram';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { COLORS, FONT_DISPLAY } from '../../styles/theme';
import { googleMapsUrl } from '../lists/SharedListPage';
import { PROGRAM_META } from '../lists/programMeta';
import { STATUS_COLORS } from '../../utils/markers';
import { formatDistance } from '../../utils/geo';

const CATEGORY_LABEL = { mustgo: 'Must go', luxury: 'Luxury', healthy: 'Healthy' };

export default function PlaceDetailSheet({
  place, open, onClose, onEdit, onDelete, onToggleVisited, onAddToList, distanceKm,
}) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);

  if (!place) {
    return (
      <SwipeableDrawer anchor="bottom" open={false} onOpen={() => {}} onClose={onClose} />
    );
  }

  const isProgram = place._source === 'program';
  const programs = place.programs || [];
  const hasPhoto = !!place.photoURL;
  const categoryColor = STATUS_COLORS[place.category];

  const handleAdd = async () => {
    if (!onAddToList) return;
    setAdding(true);
    try {
      await onAddToList(place);
      setAdded(true);
      setTimeout(() => { setAdded(false); onClose(); }, 900);
    } finally {
      setAdding(false);
    }
  };

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      disableSwipeToOpen
      disableDiscovery
      swipeAreaWidth={0}
      ModalProps={{ keepMounted: false }}
      PaperProps={{
        sx: {
          bgcolor: COLORS.surface,
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
          border: `1px solid ${COLORS.divider}`,
          borderBottom: 'none',
          maxHeight: '82vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          // Avoid iOS safe-area at the bottom (home indicator)
          pb: 'env(safe-area-inset-bottom)',
        },
      }}
    >
      {/* Grab handle */}
      <Box
        onClick={onClose}
        sx={{
          display: 'flex', justifyContent: 'center',
          pt: 1, pb: 0.5, cursor: 'pointer',
        }}
      >
        <Box sx={{
          width: 44, height: 4, borderRadius: 2,
          bgcolor: 'rgba(244,239,233,0.18)',
        }} />
      </Box>

      {/* Scrollable content */}
      <Box sx={{ overflowY: 'auto', flex: 1, px: 2.25, pt: 0.5, pb: 2 }}>
        {hasPhoto && (
          <Box sx={{
            position: 'relative', mb: 1.75,
            borderRadius: 2.5, overflow: 'hidden',
            bgcolor: 'rgba(255,255,255,0.04)',
          }}>
            <Box
              component="img"
              src={place.photoURL}
              alt={place.name || ''}
              onError={e => { e.currentTarget.style.display = 'none'; }}
              sx={{
                width: '100%',
                height: 180,
                objectFit: 'cover',
                display: 'block',
              }}
            />
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                position: 'absolute', top: 8, right: 8,
                bgcolor: 'rgba(13,13,20,0.6)',
                color: '#fff',
                '&:hover': { bgcolor: 'rgba(13,13,20,0.85)' },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        {/* Header row — no photo variant gets its own close button up top */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{
              fontFamily: FONT_DISPLAY,
              fontWeight: 600,
              fontSize: '1.35rem',
              lineHeight: 1.15,
              letterSpacing: '-0.015em',
              color: COLORS.textPrimary,
            }}>
              {place.name || 'Untitled'}
            </Typography>

            {/* Cuisine · distance */}
            {(place.cuisine || distanceKm != null) && (
              <Typography sx={{
                mt: 0.4, fontSize: '0.84rem',
                color: COLORS.textSecondary,
              }}>
                {[place.cuisine, distanceKm != null ? formatDistance(distanceKm) : null]
                  .filter(Boolean).join('  ·  ')}
              </Typography>
            )}
          </Box>

          {!hasPhoto && (
            <IconButton onClick={onClose} size="small" sx={{ color: COLORS.textSecondary, mt: -0.5 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}

          {!isProgram && (
            <IconButton
              size="small"
              onClick={e => setMenuAnchor(e.currentTarget)}
              sx={{ color: COLORS.textSecondary, mt: -0.5 }}
            >
              <MoreHorizIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Status chips row */}
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1.25, alignItems: 'center' }}>
          {place.visited && (
            <Chip
              size="small"
              icon={<CheckCircleIcon sx={{ fontSize: 14, color: `${COLORS.accent} !important` }} />}
              label={place.visitedRating != null ? `Visited · ${place.visitedRating}★` : 'Visited'}
              sx={{
                height: 24, fontSize: '0.72rem', fontWeight: 600,
                bgcolor: 'rgba(227,105,80,0.14)',
                color: COLORS.accent,
                border: `1px solid rgba(227,105,80,0.3)`,
              }}
            />
          )}
          {!place.visited && !isProgram && (
            <Chip
              size="small"
              label="Want to try"
              sx={{
                height: 24, fontSize: '0.72rem', fontWeight: 600,
                bgcolor: 'rgba(240,165,147,0.1)',
                color: COLORS.accentSoft,
                border: `1px solid rgba(240,165,147,0.22)`,
              }}
            />
          )}
          {place.category && CATEGORY_LABEL[place.category] && (
            <Chip
              size="small"
              label={CATEGORY_LABEL[place.category]}
              sx={{
                height: 24, fontSize: '0.72rem', fontWeight: 600,
                bgcolor: 'transparent',
                color: categoryColor || COLORS.textSecondary,
                border: `1px solid ${categoryColor || 'rgba(255,255,255,0.14)'}`,
              }}
            />
          )}
          {programs.map(p => {
            const m = PROGRAM_META[p];
            if (!m) return null;
            return (
              <Chip
                key={p}
                size="small"
                label={m.short}
                sx={{
                  height: 24, fontSize: '0.72rem', fontWeight: 600,
                  bgcolor: m.bg,
                  color: m.marker,
                  border: `1px solid ${m.marker}`,
                }}
              />
            );
          })}
        </Box>

        {/* Address */}
        {place.address && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.6, mt: 1.5 }}>
            <LocationOnIcon sx={{ fontSize: 15, color: COLORS.textTertiary, mt: 0.1 }} />
            <Typography sx={{
              fontSize: '0.8rem', color: COLORS.textSecondary,
              lineHeight: 1.45,
            }}>
              {place.address}
            </Typography>
          </Box>
        )}

        {/* Primary action: Directions */}
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<DirectionsIcon />}
            component="a"
            href={googleMapsUrl(place)}
            target="_blank"
            rel="noopener"
            sx={{ py: 1.1, fontSize: '0.88rem', fontWeight: 700 }}
          >
            Directions
          </Button>
          {place.instagram && (
            <IconButton
              component="a"
              href={place.instagram}
              target="_blank"
              rel="noopener"
              sx={{
                width: 46, height: 46,
                border: `1px solid ${COLORS.divider}`,
                color: COLORS.rose,
                '&:hover': { bgcolor: 'rgba(200,127,129,0.08)' },
              }}
            >
              <InstagramIcon fontSize="small" />
            </IconButton>
          )}
          {place.website && (
            <IconButton
              component="a"
              href={place.website}
              target="_blank"
              rel="noopener"
              sx={{
                width: 46, height: 46,
                border: `1px solid ${COLORS.divider}`,
                color: COLORS.textSecondary,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
              }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Secondary action: visited toggle for personal places */}
        {!isProgram && (
          <Button
            fullWidth
            variant="outlined"
            onClick={() => onToggleVisited && onToggleVisited(place)}
            startIcon={place.visited
              ? <CheckCircleIcon sx={{ color: COLORS.accent }} />
              : <CheckCircleOutlineIcon />}
            sx={{
              mt: 1, py: 0.9,
              borderColor: COLORS.divider,
              color: COLORS.textPrimary,
              '&:hover': { borderColor: 'rgba(255,255,255,0.18)', bgcolor: 'rgba(255,255,255,0.03)' },
            }}
          >
            {place.visited ? 'Mark as not visited' : 'Mark as visited'}
          </Button>
        )}

        {/* Add to list for program places */}
        {isProgram && onAddToList && (
          <Button
            fullWidth
            variant="outlined"
            onClick={handleAdd}
            disabled={adding || added}
            sx={{
              mt: 1, py: 0.9,
              borderColor: COLORS.divider,
              color: COLORS.textPrimary,
              '&:hover': { borderColor: 'rgba(255,255,255,0.18)', bgcolor: 'rgba(255,255,255,0.03)' },
            }}
          >
            {added ? '✓ Added to your list' : adding ? 'Adding…' : '+ Add to my list'}
          </Button>
        )}

        {/* Rating detail */}
        {place.visited && place.visitedRating != null && (
          <Box sx={{ mt: 1.75, display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Rating
              value={Number(place.visitedRating) || 0}
              readOnly
              size="small"
              sx={{ '& .MuiRating-iconFilled': { color: COLORS.gold } }}
            />
            <Typography sx={{ fontSize: '0.76rem', color: COLORS.textTertiary }}>
              Your rating
            </Typography>
          </Box>
        )}

        {/* Notes */}
        {place.visitedNotes && (
          <Box sx={{ mt: 1.75 }}>
            <Typography variant="overline" sx={{ color: COLORS.textTertiary }}>Your notes</Typography>
            <Typography sx={{
              fontSize: '0.86rem', color: COLORS.textSecondary,
              lineHeight: 1.55, whiteSpace: 'pre-wrap',
              fontStyle: 'italic',
            }}>
              {place.visitedNotes}
            </Typography>
          </Box>
        )}

        {place.remarks && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="overline" sx={{ color: COLORS.textTertiary }}>Remarks</Typography>
            <Typography sx={{
              fontSize: '0.86rem', color: COLORS.textSecondary,
              lineHeight: 1.55, whiteSpace: 'pre-wrap',
            }}>
              {place.remarks}
            </Typography>
          </Box>
        )}

        {place.notes && !place.remarks && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="overline" sx={{ color: COLORS.textTertiary }}>Notes</Typography>
            <Typography sx={{
              fontSize: '0.86rem', color: COLORS.textSecondary,
              lineHeight: 1.55, whiteSpace: 'pre-wrap',
            }}>
              {place.notes}
            </Typography>
          </Box>
        )}

        {place.instagramCaption && (
          <Box sx={{ mt: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.4 }}>
              <InstagramIcon sx={{ fontSize: 12, color: COLORS.rose }} />
              <Typography variant="overline" sx={{ color: COLORS.rose }}>Caption</Typography>
            </Box>
            <Typography sx={{
              fontSize: '0.82rem', color: COLORS.textSecondary,
              lineHeight: 1.55, whiteSpace: 'pre-wrap',
              display: '-webkit-box', WebkitLineClamp: 8, WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}>
              {place.instagramCaption}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Menu for personal places */}
      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 190 } } }}
      >
        <MenuItem dense onClick={() => { setMenuAnchor(null); onEdit && onEdit(place); }}>
          <ListItemIcon sx={{ minWidth: 28 }}>
            <EditIcon sx={{ fontSize: 16, color: COLORS.textSecondary }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.84rem' }}>Edit</ListItemText>
        </MenuItem>
        <Divider sx={{ my: 0.3 }} />
        <MenuItem
          dense
          onClick={() => { setMenuAnchor(null); onDelete && onDelete(place); onClose(); }}
          sx={{ color: '#d16f6f' }}
        >
          <ListItemIcon sx={{ minWidth: 28 }}>
            <DeleteOutlineIcon sx={{ fontSize: 17, color: '#d16f6f' }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.84rem' }}>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </SwipeableDrawer>
  );
}
